import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_QUESTIONNAIRE_TEMPLATE } from '../../../graphql/mutations';
import { GET_QUESTIONNAIRE_TEMPLATES } from '../../../graphql/queries';
import {
  CreateQuestionnaireTemplateInput,
  QuestionnaireTemplate
} from '../../../types/questionnaireTemplates.types';

interface UseQuestionnaireTemplatesProps {
  activeTemplateId?: string;
}

export function useQuestionnaireTemplates(
  props?: UseQuestionnaireTemplatesProps
) {
  const { activeTemplateId } = props || {};
  const navigate = useNavigate();
  const { data, error, loading, refetch } = useQuery(
    GET_QUESTIONNAIRE_TEMPLATES,
    {
      fetchPolicy: activeTemplateId ? 'cache-first' : 'cache-and-network'
    }
  );

  const [createTemplate] = useMutation(CREATE_QUESTIONNAIRE_TEMPLATE, {
    onCompleted: res => {
      toast.success('Questionnaire template created');
      refetch();
      navigate(
        `/questionnaire-templates/${res?.createQuestionnaireTemplate?.questionnaireTemplate.id}`
      );
    },
    onError: () => {
      toast.error('Failed to create questionnaire template');
    }
  });

  const handleCreate = useCallback(
    async (input: CreateQuestionnaireTemplateInput) => {
      // Remove whitespace from the start and end of the templateData before sending it to the server
      const trimmedData = JSON.parse(input.templateData.trim());
      await createTemplate({
        variables: {
          input: {
            ...input,
            templateData: JSON.stringify(trimmedData)
          }
        }
      });
    },
    [createTemplate]
  );

  const handleTemplateClick = useCallback(
    (id: string) => {
      navigate(`/questionnaire-templates/${id}`);
    },
    [navigate]
  );

  const templates: QuestionnaireTemplate[] = useMemo(() => {
    return (
      data?.questionnaireTemplates?.edges?.map(
        (e: { node: QuestionnaireTemplate }) => e.node
      ) || []
    );
  }, [data]);

  const activeTemplate = useMemo(() => {
    return templates.find(({ id }) => id === activeTemplateId);
  }, [activeTemplateId, templates]);

  return {
    activeTemplate,
    error,
    handleCreate,
    handleTemplateClick,
    loading,
    templates
  };
}
